import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Measurement } from '../types';

interface TemperaturePieChartProps {
  measurements: Measurement[];
  title: string;
  type: 'measured' | 'perceived';
}

const TemperaturePieChart: React.FC<TemperaturePieChartProps> = ({ measurements, title, type }) => {
  const getTemperatureCategory = (temp: number) => {
    if (temp < 6) return '0-6°C';
    if (temp < 11) return '6-11°C';
    if (temp < 15) return '11-15°C';
    if (temp < 19) return '15-19°C';
    if (temp < 22) return '19-22°C';
    if (temp < 26) return '22-26°C';
    if (temp < 32) return '26-32°C';
    if (temp < 37) return '32-37°C';
    return '>37°C';
  };

  const getPerceivedCategory = (perceived: string) => {
    return perceived;
  };

  const getTemperatureColor = (temp: number) => {
    if (temp < 6) return '#284496';      // Sehr kalt
    if (temp < 11) return '#5d6ab0';     // Kalt
    if (temp < 15) return '#9498cc';     // Kühl
    if (temp < 19) return '#cacbe7';     // Leicht kühl
    if (temp < 22) return '#ffffff';     // Behaglich
    if (temp < 26) return '#f0cdbe';     // Leicht warm
    if (temp < 32) return '#e09a80';     // Warm
    if (temp < 37) return '#cf674a';     // Heiss
    return '#be331e';                    // Sehr heiss
  };

  const getPerceivedColor = (perceived: string) => {
    switch (perceived) {
      case 'Sehr kalt': return '#284496';
      case 'Kalt': return '#5d6ab0';
      case 'Kühl': return '#9498cc';
      case 'Leicht kühl': return '#cacbe7';
      case 'Behaglich': return '#ffffff';
      case 'Leicht warm': return '#f0cdbe';
      case 'Warm': return '#e09a80';
      case 'Heiss': return '#cf674a';
      case 'Sehr heiss': return '#be331e';
      default: return '#cccccc';
    }
  };

  // Process data
  const data = React.useMemo(() => {
    const categoryCount: { [key: string]: number } = {};
    
    console.log('Pie Chart Debug:', {
      type,
      totalMeasurements: measurements.length,
      sampleMeasurements: measurements.slice(0, 10).map(m => ({
        measured: m.measured_temperature,
        perceived: m.perceived_temperature,
        id: m.id
      }))
    });
    
    let processedCount = 0;
    let skippedCount = 0;
    
    measurements.forEach(measurement => {
      let category: string;
      
      if (type === 'measured') {
        // For measured temperature chart, only validate measured temperature
        if (measurement.measured_temperature == null || measurement.measured_temperature < 0) {
          console.log('Skipping invalid measured temperature:', measurement.measured_temperature, 'for measurement ID:', measurement.id);
          skippedCount++;
          return;
        }
        category = getTemperatureCategory(measurement.measured_temperature);
        processedCount++;
      } else {
        // For perceived temperature chart, only validate perceived temperature
        if (!measurement.perceived_temperature || measurement.perceived_temperature.trim() === '') {
          console.log('Skipping invalid perceived temperature:', measurement.perceived_temperature, 'for measurement ID:', measurement.id);
          skippedCount++;
          return;
        }
        category = getPerceivedCategory(measurement.perceived_temperature);
        processedCount++;
      }
      
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    });

    console.log('Category processing:', {
      processedCount,
      skippedCount,
      categoryCount,
      allCategories: Object.keys(categoryCount)
    });

    // Only include categories that have data
    const result = Object.entries(categoryCount)
      .filter(([name, value]) => {
        const hasData = value > 0;
        if (!hasData) {
          console.log('Filtering out category with no data:', name, 'value:', value);
        }
        return hasData;
      })
      .map(([name, value]) => {
        console.log('Processing category with data:', name, 'value:', value);
        const color = type === 'measured' 
          ? getTemperatureColor(measurements.find(m => 
              m.measured_temperature != null && 
              m.measured_temperature >= 0 && 
              getTemperatureCategory(m.measured_temperature) === name
            )?.measured_temperature || 20)
          : getPerceivedColor(name);
        
        console.log('Color assigned for', name, ':', color);
        
        return {
          name,
          value,
          color
        };
      })
      .filter(item => item.value > 0) // Final safety check
      .sort((a, b) => b.value - a.value); // Sort by value descending
    
    // Add "Keine Daten" segment if there are any problematic entries
    const finalResult = [...result];
    
    // Check if any entries have white/transparent colors or very small values
    const problematicEntries = result.filter(item => 
      item.color === '#ffffff' || 
      item.color === 'white' || 
      item.color === 'transparent' ||
      item.value < 1
    );
    
    if (problematicEntries.length > 0) {
      console.log('Found problematic entries:', problematicEntries);
      // Remove problematic entries and add "Keine Daten" segment
      const cleanResult = result.filter(item => 
        item.color !== '#ffffff' && 
        item.color !== 'white' && 
        item.color !== 'transparent' &&
        item.value >= 1
      );
      
      finalResult.length = 0; // Clear array
      finalResult.push(...cleanResult);
      finalResult.push({
        name: 'Keine Daten',
        value: problematicEntries.reduce((sum, item) => sum + item.value, 0),
        color: '#d1d5db' // Lighter grey for no data
      });
    }
      
    console.log('Pie Chart Final Data:', {
      categoryCount,
      result: finalResult,
      resultLength: finalResult.length,
      totalProcessed: Object.values(categoryCount).reduce((a, b) => a + b, 0),
      allValues: finalResult.map(item => ({ name: item.name, value: item.value, color: item.color })),
      hasZeroValues: finalResult.some(item => item.value === 0),
      zeroValueItems: finalResult.filter(item => item.value === 0),
      problematicEntries: problematicEntries.length
    });
    
    // Final safety check - if no data, return empty array
    if (finalResult.length === 0) {
      console.log('No valid data found for pie chart');
      return [];
    }
    
    return finalResult;
  }, [measurements, type]);

  if (data.length === 0) {
    console.log('Pie chart: No data available, showing empty state');
    return (
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          Keine Daten verfügbar
        </div>
      </div>
    );
  }

  console.log('Pie chart rendering with data:', {
    title,
    dataLength: data.length,
    data: data.map(item => ({ name: item.name, value: item.value }))
  });

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={false} // Remove labels from pie slices
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number, name: string) => [`${value} Messungen`, name]}/>
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TemperaturePieChart;
